import * as React from 'react';
import { Box, Popover, Stack, Typography, IconButton, FormHelperText } from '@mui/material';
import { CalendarBlank, CaretLeft, CaretRight, Clock } from '@phosphor-icons/react';
import moment from 'moment';
import { styled } from '@mui/material/styles';

interface CalendarDatetimeRangeProps {
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startError?: string;
  endError?: string;
}

const MINI_CALENDAR_WIDTH = 256;
const TIME_DROPDOWN_WIDTH = 120;
const TIME_DROPDOWN_MAX_HEIGHT = 240;
const DATE_COL_FLEX = '0 0 40%';
const TIME_COL_FLEX = '0 0 calc(30% - 12px)';

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? 0 : 30;
  return moment().hours(h).minutes(m).seconds(0).format('h:mm A');
});

const setDateInValue = (currentValue: string, newDate: moment.Moment): string => {
  const t = moment(currentValue, 'YYYY-MM-DDTHH:mm');
  return newDate.clone().hours(t.isValid() ? t.hours() : 0).minutes(t.isValid() ? t.minutes() : 0).format('YYYY-MM-DDTHH:mm');
};

const setTimeInValue = (currentValue: string, timeOption: string): string => {
  const d = moment(currentValue, 'YYYY-MM-DDTHH:mm');
  const t = moment(timeOption, 'h:mm A');
  return d.clone().hours(t.hours()).minutes(t.minutes()).format('YYYY-MM-DDTHH:mm');
};

const nearestTimeOption = (value: string): string => {
  const m = moment(value, 'YYYY-MM-DDTHH:mm');
  if (!m.isValid()) return '12:00 PM';
  const totalMins = m.hours() * 60 + m.minutes();
  const idx = Math.round(totalMins / 30) % 48;
  return TIME_OPTIONS[idx];
};

const parseDateInput = (raw: string, currentValue: string): string | null => {
  const trimmed = raw.trim();
  const currentYear = moment(currentValue, 'YYYY-MM-DDTHH:mm').year() || moment().year();
  const withYear = `${trimmed} ${currentYear}`;
  for (const fmt of ['ddd, MMM D YYYY', 'MMM D YYYY', 'M/D YYYY', 'MM/DD YYYY']) {
    const m = moment(withYear, fmt, true);
    if (m.isValid()) return setDateInValue(currentValue, m);
  }
  for (const fmt of ['M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'MMM D, YYYY']) {
    const m = moment(trimmed, fmt, true);
    if (m.isValid()) return setDateInValue(currentValue, m);
  }
  return null;
};

const parseTimeInput = (raw: string, currentValue: string): string | null => {
  const trimmed = raw.trim();
  for (const fmt of ['h:mm a', 'h:mma', 'h:mm A', 'h:mmA', 'h a', 'ha', 'hA', 'H:mm', 'HH:mm']) {
    const m = moment(trimmed, fmt, true);
    if (m.isValid()) return setTimeInValue(currentValue, m.format('h:mm A'));
  }
  return null;
};

const MiniCalendar = ({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (date: moment.Moment) => void;
  onClose: () => void;
}) => {
  const selected = moment(value, 'YYYY-MM-DDTHH:mm');
  const today = moment().startOf('day');
  const [viewing, setViewing] = React.useState(() =>
    (selected.isValid() ? selected : moment()).clone().startOf('month')
  );

  const gridStart = viewing.clone().startOf('week');
  const weeks: moment.Moment[][] = [];
  const cur = gridStart.clone();
  for (let w = 0; w < 6; w++) {
    const week: moment.Moment[] = [];
    for (let d = 0; d < 7; d++) { week.push(cur.clone()); cur.add(1, 'day'); }
    weeks.push(week);
  }

  return (
    <Box sx={{ p: 1.5, width: MINI_CALENDAR_WIDTH }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <IconButton size="small" onClick={() => setViewing(v => v.clone().subtract(1, 'month'))}>
          <CaretLeft size={14} />
        </IconButton>
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
          {viewing.format('MMMM YYYY')}
        </Typography>
        <IconButton size="small" onClick={() => setViewing(v => v.clone().add(1, 'month'))}>
          <CaretRight size={14} />
        </IconButton>
      </Stack>

      <Stack direction="row">
        {['S','M','T','W','T','F','S'].map((d, i) => (
          <Box key={i} sx={{ flex: 1, textAlign: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{d}</Typography>
          </Box>
        ))}
      </Stack>

      {weeks.map((week, wi) => (
        <Stack key={wi} direction="row">
          {week.map((day, di) => {
            const isSelected = selected.isValid() && day.isSame(selected, 'day');
            const isToday = day.isSame(today, 'day');
            const isCurrentMonth = day.month() === viewing.month();
            return (
              <Box
                key={di}
                onClick={() => { onChange(day); onClose(); }}
                sx={{
                  flex: 1, textAlign: 'center', py: 0.375, borderRadius: '50%',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'primary.main' : 'transparent',
                  '&:hover': { backgroundColor: isSelected ? 'primary.dark' : 'action.hover' },
                }}
              >
                <Typography sx={{
                  fontSize: 12,
                  fontWeight: isToday ? 700 : 400,
                  color: isSelected ? 'common.white' : !isCurrentMonth ? 'text.disabled' : isToday ? 'primary.main' : 'text.primary',
                }}>
                  {day.format('D')}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      ))}
    </Box>
  );
};

const TimeDropdown = ({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (time: string) => void;
  onClose: () => void;
}) => {
  const currentOption = nearestTimeOption(value);
  const selectedRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) node.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <Box sx={{ width: TIME_DROPDOWN_WIDTH, maxHeight: TIME_DROPDOWN_MAX_HEIGHT, overflowY: 'auto', py: 0.5 }}>
      {TIME_OPTIONS.map((opt) => {
        const isSelected = opt === currentOption;
        return (
          <Box
            key={opt}
            ref={isSelected ? selectedRef : undefined}
            onClick={() => { onChange(opt); onClose(); }}
            sx={{
              px: 2, py: 0.75, cursor: 'pointer', fontSize: 13,
              backgroundColor: isSelected ? 'primary.main' : 'transparent',
              color: isSelected ? 'common.white' : 'text.primary',
              '&:hover': { backgroundColor: isSelected ? 'primary.dark' : 'action.hover' },
            }}
          >
            {opt}
          </Box>
        );
      })}
    </Box>
  );
};

type AnchorType = 'start-date' | 'start-time' | 'end-date' | 'end-time';

const FieldWrapper = styled(Box, {
  shouldForwardProp: (p) => p !== 'hasError',
})<{ hasError?: boolean }>(({ theme, hasError }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.grey[200]}`,
  transition: theme.transitions.create(['border-color', 'box-shadow']),
  overflow: 'hidden',
  '&:hover': { borderColor: hasError ? theme.palette.error.main : theme.palette.grey[300] },
  '&:focus-within': {
    borderColor: hasError ? theme.palette.error.main : theme.palette.secondary.dark,
    boxShadow: `0 0 0 1px ${hasError ? theme.palette.error.main : theme.palette.secondary.dark}`,
  },
}));

const FieldInput = styled('input')(({ theme }) => ({
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: theme.spacing(1, 0.5, 1, 2),
  fontSize: theme.typography.body1.fontSize,
  lineHeight: theme.typography.body1.lineHeight,
  fontWeight: 500,
  color: theme.palette.text.primary,
  width: '100%',
  minWidth: 0,
  fontFamily: 'inherit',
  cursor: 'text',
  '&:focus, &:focus-visible': { outline: 'none' },
  '&::placeholder': { color: theme.palette.text.disabled, fontWeight: 400 },
}));

export const CalendarDatetimeRange = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startError,
  endError,
}: CalendarDatetimeRangeProps) => {
  const [anchorType, setAnchorType] = React.useState<AnchorType | null>(null);
  const [editing, setEditing] = React.useState<{ field: AnchorType; raw: string } | null>(null);

  const startDateRef = React.useRef<HTMLDivElement>(null);
  const startTimeRef = React.useRef<HTMLDivElement>(null);
  const endDateRef = React.useRef<HTMLDivElement>(null);
  const endTimeRef = React.useRef<HTMLDivElement>(null);

  const fieldRefs: Record<AnchorType, React.RefObject<HTMLDivElement>> = {
    'start-date': startDateRef,
    'start-time': startTimeRef,
    'end-date': endDateRef,
    'end-time': endTimeRef,
  };

  const openPicker = (type: AnchorType) => setAnchorType(type);
  const closePicker = () => setAnchorType(null);

  const startMoment = moment(startValue, 'YYYY-MM-DDTHH:mm');
  const endMoment = moment(endValue, 'YYYY-MM-DDTHH:mm');
  const sameDay = startMoment.isValid() && endMoment.isValid() && startMoment.isSame(endMoment, 'day');

  const bumpEnd = (newStart: string) => {
    const ns = moment(newStart, 'YYYY-MM-DDTHH:mm');
    const ne = moment(endValue, 'YYYY-MM-DDTHH:mm');
    if (ns.isValid() && ne.isValid() && !ne.isAfter(ns)) {
      onEndChange(ns.clone().add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
    }
  };

  const handleStartDateChange = (date: moment.Moment) => {
    const newStart = setDateInValue(startValue, date);
    onStartChange(newStart);
    bumpEnd(newStart);
  };

  const handleStartTimeChange = (time: string) => {
    const newStart = setTimeInValue(startValue, time);
    onStartChange(newStart);
    bumpEnd(newStart);
  };

  const handleEndDateChange = (date: moment.Moment) => {
    onEndChange(setDateInValue(endValue, date));
  };

  const handleEndTimeChange = (time: string) => {
    onEndChange(setTimeInValue(endValue, time));
  };

  const handleFocus = (field: AnchorType, displayStr: string) => {
    setEditing({ field, raw: displayStr });
  };

  const handleChange = (raw: string) => {
    if (editing) setEditing({ ...editing, raw });
  };

  const handleCommit = (field: AnchorType) => {
    if (!editing || editing.field !== field) return;
    const { raw } = editing;
    setEditing(null);
    switch (field) {
      case 'start-date': {
        const parsed = parseDateInput(raw, startValue);
        if (parsed) { onStartChange(parsed); bumpEnd(parsed); }
        break;
      }
      case 'start-time': {
        const parsed = parseTimeInput(raw, startValue);
        if (parsed) { onStartChange(parsed); bumpEnd(parsed); }
        break;
      }
      case 'end-date': {
        const parsed = parseDateInput(raw, endValue);
        if (parsed) onEndChange(parsed);
        break;
      }
      case 'end-time': {
        const parsed = parseTimeInput(raw, endValue);
        if (parsed) onEndChange(parsed);
        break;
      }
    }
  };

  const startDateStr = startMoment.isValid() ? startMoment.format('ddd, MMM D') : '';
  const startTimeStr = startMoment.isValid() ? startMoment.format('h:mma') : '';
  const endDateStr = endMoment.isValid() ? endMoment.format('ddd, MMM D') : '';
  const endTimeStr = endMoment.isValid() ? endMoment.format('h:mma') : '';

  const displayValue = (field: AnchorType, str: string) =>
    editing?.field === field ? editing.raw : str;

  const renderField = (
    field: AnchorType,
    displayStr: string,
    placeholder: string,
    icon: React.ReactNode,
    ref: React.RefObject<HTMLDivElement>,
    hasError: boolean,
  ) => (
    <FieldWrapper ref={ref} hasError={hasError}>
      <FieldInput
        value={displayValue(field, displayStr)}
        placeholder={placeholder}
        onFocus={() => handleFocus(field, displayStr)}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => handleCommit(field)}
        onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
      />
      <IconButton
        size="small"
        tabIndex={-1}
        sx={{ mr: 0.5, flexShrink: 0, color: 'text.secondary' }}
        onClick={() => openPicker(field)}
      >
        {icon}
      </IconButton>
    </FieldWrapper>
  );

  return (
    <Box>
      <Stack direction="row" gap={1.5} alignItems="flex-end">
        <Box sx={{ flex: DATE_COL_FLEX }}>
          <Typography className="text-input-label" sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
            Date
          </Typography>
          {renderField('start-date', startDateStr, 'Pick date', <CalendarBlank size={14} />, startDateRef, !!startError)}
        </Box>

        <Box sx={{ flex: TIME_COL_FLEX }}>
          <Typography className="text-input-label" sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
            Start time
          </Typography>
          {renderField('start-time', startTimeStr, 'Pick time', <Clock size={14} />, startTimeRef, !!startError)}
        </Box>

        <Box sx={{ flex: TIME_COL_FLEX }}>
          <Typography className="text-input-label" sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
            End time
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {!sameDay && renderField('end-date', endDateStr, 'Pick date', <CalendarBlank size={14} />, endDateRef, !!endError)}
            {renderField('end-time', endTimeStr, 'Pick time', <Clock size={14} />, endTimeRef, !!endError)}
          </Stack>
        </Box>
      </Stack>

      {(startError || endError) && (
        <FormHelperText error sx={{ mt: 0.5, ml: 0.5 }}>
          {startError || endError}
        </FormHelperText>
      )}

      <Popover
        open={!!anchorType}
        anchorEl={anchorType ? fieldRefs[anchorType].current : null}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 2, mt: 0.5, boxShadow: 3 } }}
      >
        {anchorType === 'start-date' && (
          <MiniCalendar value={startValue} onChange={handleStartDateChange} onClose={closePicker} />
        )}
        {anchorType === 'start-time' && (
          <TimeDropdown value={startValue} onChange={handleStartTimeChange} onClose={closePicker} />
        )}
        {anchorType === 'end-date' && (
          <MiniCalendar value={endValue} onChange={handleEndDateChange} onClose={closePicker} />
        )}
        {anchorType === 'end-time' && (
          <TimeDropdown value={endValue} onChange={handleEndTimeChange} onClose={closePicker} />
        )}
      </Popover>
    </Box>
  );
};
